/* ------------------------------------------------------------------
   tracking.js — Meta Pixel + GA4 event helpers
   ------------------------------------------------------------------
   Safe by design: every call checks the tracker exists first, so the
   site works normally if the Pixel is not installed yet, if an ad
   blocker removes it, or during the react-snap prerender build.
------------------------------------------------------------------- */

const CURRENCY = 'PKR';

const isBrowser = () => typeof window !== 'undefined';

/* Meta Pixel */
function fb(event, data, custom) {
  if (!isBrowser() || typeof window.fbq !== 'function') return;
  try {
    window.fbq(custom ? 'trackCustom' : 'track', event, data || {});
  } catch (e) {
    /* never let tracking break the UI */
  }
}

/* GA4 */
function ga(event, data) {
  if (!isBrowser() || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', event, data || {});
  } catch (e) {
    /* never let tracking break the UI */
  }
}

/* ---------------- Page view (SPA route change) ---------------- */
export function trackPageView(pageName) {
  fb('PageView');
  ga('page_view', {
    page_title: pageName,
    page_location: isBrowser() ? window.location.href : '',
    page_path: isBrowser() ? window.location.pathname : '',
  });
}

/* ---------------- WhatsApp contact (your main conversion) ------ */
export function trackWhatsApp(source) {
  fb('Contact', { content_name: source || 'whatsapp' });
  ga('whatsapp_click', { source: source || 'general' });
  ga('generate_lead', { source: source || 'general' });
}

/* ---------------- Product viewed ------------------------------- */
export function trackViewProduct(product) {
  if (!product) return;
  fb('ViewContent', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_category: product.cat || '',
    content_type: 'product',
    value: Number(product.price) || 0,
    currency: CURRENCY,
  });
  ga('view_item', {
    currency: CURRENCY,
    value: Number(product.price) || 0,
    items: [{
      item_id: String(product.id),
      item_name: product.name,
      item_category: product.cat || '',
      price: Number(product.price) || 0,
    }],
  });
}

/* ---------------- Added to cart -------------------------------- */
export function trackAddToCart(product) {
  if (!product) return;
  fb('AddToCart', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_category: product.cat || '',
    content_type: 'product',
    value: Number(product.price) || 0,
    currency: CURRENCY,
  });
  ga('add_to_cart', {
    currency: CURRENCY,
    value: Number(product.price) || 0,
    items: [{
      item_id: String(product.id),
      item_name: product.name,
      item_category: product.cat || '',
      price: Number(product.price) || 0,
      quantity: 1,
    }],
  });
}

/* ---------------- Checkout started ----------------------------- */
export function trackBeginCheckout(cart, total) {
  const items = (cart || []).map((i) => ({
    item_id: String(i.id),
    item_name: i.name,
    item_category: i.cat || '',
    price: Number(i.price) || 0,
    quantity: i.qty || 1,
  }));
  fb('InitiateCheckout', {
    content_ids: items.map((i) => i.item_id),
    contents: (cart || []).map((i) => ({ id: String(i.id), quantity: i.qty || 1 })),
    num_items: items.reduce((s, i) => s + i.quantity, 0),
    value: Number(total) || 0,
    currency: CURRENCY,
  });
  ga('begin_checkout', { currency: CURRENCY, value: Number(total) || 0, items });
}

/* ---------------- Order placed (sent to WhatsApp) -------------- */
export function trackPurchase(cart, total, orderId) {
  const items = (cart || []).map((i) => ({
    item_id: String(i.id),
    item_name: i.name,
    item_category: i.cat || '',
    price: Number(i.price) || 0,
    quantity: i.qty || 1,
  }));
  fb('Purchase', {
    content_ids: items.map((i) => i.item_id),
    contents: (cart || []).map((i) => ({ id: String(i.id), quantity: i.qty || 1 })),
    num_items: items.reduce((s, i) => s + i.quantity, 0),
    value: Number(total) || 0,
    currency: CURRENCY,
  });
  ga('purchase', {
    transaction_id: orderId || ('order_' + Date.now()),
    currency: CURRENCY,
    value: Number(total) || 0,
    items,
  });
}

/* ---------------- Search / filter use -------------------------- */
export function trackSearch(term) {
  if (!term) return;
  fb('Search', { search_string: term });
  ga('search', { search_term: term });
}

/* ---------------- Signup / login ------------------------------- */
export function trackSignUp(method) {
  fb('CompleteRegistration', { status: true });
  ga('sign_up', { method: method || 'email' });
}
